import { useTranslation } from 'react-i18next';
import { useGameStore } from '../../store/game-store';
import { Color, GameStatus } from '@draughts/shared';
import { endReasonText } from '../../utils/end-reason-text';
import BlindPeekSummary from './BlindPeekSummary';
import './GameOverModal.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface GameOverModalProps {
  customTitle?: string;
  customMessage?: string;
  onNewGame?: () => void;
  /**
   * Sostituisce "Nuova partita" quando c'e' qualcosa di piu' preciso da offrire:
   * a fine andata di un match in presenza, il ritorno.
   */
  primaryLabel?: string;
  onLeave?: () => void;
}

function GameOverModal({ customTitle, customMessage, onNewGame, primaryLabel, onLeave }: GameOverModalProps = {}) {
  const { t } = useTranslation();
  const status = useGameStore((state) => state.status);
  const resetGame = useGameStore((state) => state.resetGame);
  const ruleMode = useGameStore((state) => state.ruleMode);
  const endReason = useGameStore((state) => state.endReason);
  /*
   * Il fuoco dentro il dialogo, e Tab che non ne esce. Prima non c'era nulla:
   * chi navigava da tastiera continuava a tabulare sulla damiera SOTTO, che è
   * ancora lì dietro il velo, e uno screen reader non annunciava che la partita
   * fosse finita.
   *
   * Dichiarato una volta e usato da entrambi i rami del `return` qui sotto: solo
   * uno dei due è montato per volta, quindi la ref appartiene sempre a un solo
   * nodo.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(true);

  // If custom props provided, show with custom content
  if (customTitle) {
    return (
      <div className="game-over-overlay">
        {/* Dialogo dichiarato + fuoco governato: vedi `useModaleAccessibile`.
            Nessun Escape — da qui si esce dalle azioni (rivincita, esci), e un
            tasto che lo chiudesse lascerebbe una partita finita senza uscita. */}
        <div
          ref={riferimentoDialogo}
          className="game-over-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="game-over-titolo"
          tabIndex={-1}
        >
          <h2 className="game-over-title" id="game-over-titolo">{customTitle}</h2>
          {customMessage && <p className="game-over-message">{customMessage}</p>}
          <div className="game-over-buttons">
            <button className="game-over-button" onClick={onNewGame ?? resetGame}>
              {primaryLabel ?? t('game.actions.newGame')}
            </button>
            {onLeave && (
              <button className="game-over-button game-over-button--secondary" onClick={onLeave}>
                {t('common.exit')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Only show modal when game is over (local mode)
  if (status === GameStatus.PLAYING || status === GameStatus.WAITING) {
    return null;
  }

  let title: string;
  let message: string;

  // Il motivo vero, registrato dallo store a fine partita: il motore per le fini che si
  // leggono dalla posizione, l'azione per abbandono / patta accordata / tempo scaduto.
  // Prima qui c'era una sola frase per tutti i casi, e dopo un abbandono diceva "il Nero
  // non ha più mosse legali".
  const loser = status === GameStatus.WHITE_WINS ? Color.BLACK : status === GameStatus.BLACK_WINS ? Color.WHITE : undefined;
  const reasonText = endReasonText(t, endReason, { loser, ruleMode });

  switch (status) {
    case GameStatus.WHITE_WINS:
      title = t('game.result.whiteWins');
      message = reasonText || t('game.endReason.gameEnded');
      break;
    case GameStatus.BLACK_WINS:
      title = t('game.result.blackWins');
      message = reasonText || t('game.endReason.gameEnded');
      break;
    case GameStatus.DRAW:
      title = t('game.result.draw');
      message = reasonText || t('game.endReason.drawEnded');
      break;
    case GameStatus.ABANDONED:
      /*
       * Due strade finiscono qui e dicono cose diverse. La partita in presenza
       * chiusa dai due giocatori non ha un vincitore e lo dichiara; una partita
       * online annullata resta quella di prima.
       *
       * A distinguerle e' la FORMA del motivo, non una prop nuova: il server lo
       * manda come stringa, lo store locale come oggetto (`LocalEndAction`) —
       * la stessa discriminante che usa `endReasonText`.
       */
      if (typeof endReason === 'object' && endReason?.kind === 'aborted') {
        title = t('game.result.noWinner');
        message = reasonText || t('game.endReason.noWinner');
        break;
      }
      title = t('game.result.gameAbandoned');
      message = t('game.endReason.gameAbandoned');
      break;
    default:
      title = t('game.result.gameOver');
      message = reasonText || t('game.endReason.gameEnded');
  }

  return (
    <div className="game-over-overlay">
      {/* Come sopra: dialogo dichiarato e fuoco governato. */}
      <div
        ref={riferimentoDialogo}
        className="game-over-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="game-over-titolo"
        tabIndex={-1}
      >
        <h2 className="game-over-title" id="game-over-titolo">{title}</h2>
        <p className="game-over-message">{message}</p>
        <BlindPeekSummary blind={ruleMode === 'blind'} />
        <div className="game-over-buttons">
          <button className="game-over-button" onClick={onNewGame ?? resetGame}>
            {primaryLabel ?? t('game.actions.newGame')}
          </button>
          {onLeave && (
            <button className="game-over-button game-over-button--secondary" onClick={onLeave}>
              {t('common.exit')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default GameOverModal;
